import { DateTime } from 'luxon'
import { apiAxios } from '@/api'
import { userUploadDocument } from '@/actions/awsActions'
import { UploadDocumentType } from '@/constants/businessConstants'
import type { Dispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { CATCH_UP_MONTHS_PATH } from '../../lib/constants'
import type { WidgetDisplay } from '../../lib/types'

export interface StatementRequest {
  financialAccountId: number
  accountLabel: string
  months: string[]
}

/** Mirrors the API's `CatchUpMonthState`. */
export const CATCH_UP_MONTH_STATE = {
  reconciled: 'reconciled',
  noStatementExpected: 'no_statement_expected',
  inProgress: 'in_progress',
  beforeFirstActivity: 'before_first_activity',
  awaitingStatement: 'awaiting_statement',
  notReconciled: 'not_reconciled',
} as const

export type CatchUpMonthState =
  (typeof CATCH_UP_MONTH_STATE)[keyof typeof CATCH_UP_MONTH_STATE]

export const SLOT_KIND = {
  open: 'open',
  uploading: 'uploading',
  received: 'received',
  onFile: 'on_file',
  caughtUp: 'caught_up',
  error: 'error',
} as const

export type SlotState =
  | { kind: typeof SLOT_KIND.open }
  | { kind: typeof SLOT_KIND.uploading; fileName: string; percent: number }
  | { kind: typeof SLOT_KIND.received; fileName: string; readable: boolean }
  | { kind: typeof SLOT_KIND.onFile }
  | { kind: typeof SLOT_KIND.caughtUp }
  | { kind: typeof SLOT_KIND.error; message: string; file: File | null }

export interface Slot {
  key: string
  financialAccountId: number
  accountLabel: string
  month: string
  state: SlotState
  /** The bank shares only recent transactions, and this month comes before them. */
  beforeBankHistory: boolean
  institutionName: string | null
  /** The day the bank's history starts, as yyyy-MM-dd. */
  bankHistoryStartsOn: string | null
  /** The account's months from its first catch-up month that the server would record as not open. */
  notOpenRun: string[]
}

export interface MonthStatus {
  state: string
  statementOnFile: boolean | null
  confirmableAsEmpty: boolean
  beforeBankHistory: boolean
  institutionName: string | null
  bankHistoryStartsOn: string | null
  notOpenRun: string[]
}

interface CatchUpStatementMonths {
  accounts: {
    financialAccountId: number
    plaidInstitutionName?: string | null
    bankHistoryStartsOn?: string | null
    /** The months the server would record as not open; empty when it would refuse the answer. */
    notOpenRun?: string[]
    months: {
      month: string
      state: string
      statementOnFile?: boolean
      confirmableAsEmpty?: boolean
      beforeBankHistory?: boolean
    }[]
  }[]
}

export const toBankHistoryNote = (
  institutionName: string | null,
  bankHistoryStartsOn: string | null
): string => {
  const bank = institutionName ?? 'Your bank'
  if (!bankHistoryStartsOn)
    return `${bank} only shares part of your history with Heard. Please upload your statements for these months so Heard can add them to your books.`
  const startsOn = DateTime.fromFormat(
    bankHistoryStartsOn,
    DATE_FORMATS_LUXON.YEAR_MONTH_DAY,
    { zone: 'utc' }
  ).toFormat(DATE_FORMATS_LUXON.MONTH_DAY)
  return `${bank} only shares your transactions from ${startsOn} on with Heard. Please upload your statements for the months before that so Heard can add them to your books.`
}

export const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

const isStatementRequest = (value: unknown): value is StatementRequest =>
  typeof value === 'object' &&
  value !== null &&
  'financialAccountId' in value &&
  typeof value.financialAccountId === 'number' &&
  'accountLabel' in value &&
  typeof value.accountLabel === 'string' &&
  'months' in value &&
  Array.isArray(value.months) &&
  value.months.every(
    (month) => typeof month === 'string' && MONTH_PATTERN.test(month)
  )

export const toRequests = (
  input: Record<string, unknown>
): StatementRequest[] =>
  Array.isArray(input.requests) ? input.requests.filter(isStatementRequest) : []

export const slotKey = (financialAccountId: number, month: string): string =>
  `${financialAccountId}:${month}`

export const toMonthDisplay = (month: string): string =>
  DateTime.fromFormat(month, DATE_FORMATS_LUXON.YEAR_MONTH).toFormat(
    DATE_FORMATS_LUXON.MONTH_YEAR_LONG
  )

export const fetchCatchUpMonthStates = async (): Promise<
  Map<string, MonthStatus>
> => {
  const { data } =
    await apiAxios.get<CatchUpStatementMonths>(CATCH_UP_MONTHS_PATH)
  const states = new Map<string, MonthStatus>()
  data.accounts.forEach((account) => {
    const {
      financialAccountId,
      plaidInstitutionName,
      bankHistoryStartsOn,
      months,
    } = account
    const notOpenRun = account.notOpenRun ?? []
    months.forEach(
      ({
        month,
        state,
        statementOnFile,
        confirmableAsEmpty,
        beforeBankHistory,
      }) =>
        states.set(slotKey(financialAccountId, month), {
          state,
          statementOnFile: statementOnFile ?? null,
          confirmableAsEmpty: confirmableAsEmpty ?? false,
          beforeBankHistory: beforeBankHistory ?? false,
          institutionName: plaidInstitutionName ?? null,
          bankHistoryStartsOn: bankHistoryStartsOn ?? null,
          notOpenRun,
        })
    )
  })
  return states
}

type InitialSlotKind =
  | typeof SLOT_KIND.open
  | typeof SLOT_KIND.onFile
  | typeof SLOT_KIND.caughtUp

const ON_FILE_IF_STATEMENT = 'on_file_if_statement'

/**
 * Keyed on the full union so a new server state has to be placed here. Only a
 * month Heard holds a statement for reads as on file; the rest are dropped.
 */
const SLOT_FOR_STATE: Record<
  CatchUpMonthState,
  InitialSlotKind | typeof ON_FILE_IF_STATEMENT | null
> = {
  [CATCH_UP_MONTH_STATE.awaitingStatement]: SLOT_KIND.open,
  [CATCH_UP_MONTH_STATE.inProgress]: SLOT_KIND.onFile,
  [CATCH_UP_MONTH_STATE.notReconciled]: ON_FILE_IF_STATEMENT,
  [CATCH_UP_MONTH_STATE.reconciled]: SLOT_KIND.caughtUp,
  [CATCH_UP_MONTH_STATE.noStatementExpected]: null,
  [CATCH_UP_MONTH_STATE.beforeFirstActivity]: null,
}

const isCatchUpMonthState = (value: string): value is CatchUpMonthState =>
  value in SLOT_FOR_STATE

const toSlotKind = (
  status: MonthStatus | undefined
): InitialSlotKind | null => {
  if (!status || !isCatchUpMonthState(status.state)) return null
  const kind = SLOT_FOR_STATE[status.state]
  if (kind !== ON_FILE_IF_STATEMENT) return kind
  // Null means an API older than the field: keep the on-file mapping it was built for.
  return status.statementOnFile === false ? null : SLOT_KIND.onFile
}

/**
 * The model can ask for a month Heard no longer needs, or an account that is
 * not this member's, so only the server's view decides which slots open.
 */
export const toSlots = (
  requests: StatementRequest[],
  states: Map<string, MonthStatus>
): Slot[] =>
  requests.flatMap(({ financialAccountId, accountLabel, months }) =>
    [...new Set(months)].flatMap((month): Slot[] => {
      const key = slotKey(financialAccountId, month)
      const status = states.get(key)
      const kind = toSlotKind(status)
      if (!kind) return []
      return [
        {
          key,
          financialAccountId,
          accountLabel,
          month,
          state: { kind },
          beforeBankHistory: status?.beforeBankHistory ?? false,
          institutionName: status?.institutionName ?? null,
          bankHistoryStartsOn: status?.bankHistoryStartsOn ?? null,
          notOpenRun: status?.notOpenRun ?? [],
        },
      ]
    })
  )

const LOCAL_KINDS: ReadonlySet<SlotState['kind']> = new Set([
  SLOT_KIND.uploading,
  SLOT_KIND.received,
  SLOT_KIND.error,
])

/** A reload keeps what this card did since it loaded, so an upload in flight is not reset. */
export const keepLocalProgress = (
  previous: readonly Slot[] | null,
  next: Slot[]
): Slot[] => {
  const local = new Map(
    (previous ?? [])
      .filter(({ state }) => LOCAL_KINDS.has(state.kind))
      .map((slot) => [slot.key, slot.state])
  )
  return next.map((slot) => {
    const state = local.get(slot.key)
    return state ? { ...slot, state } : slot
  })
}

export const isSettled = (slot: Slot): boolean =>
  slot.state.kind === SLOT_KIND.received ||
  slot.state.kind === SLOT_KIND.onFile ||
  slot.state.kind === SLOT_KIND.caughtUp

const OUTSTANDING = 'outstanding'

const listMonths = (
  slots: Slot[],
  kind: SlotState['kind'] | typeof OUTSTANDING
) =>
  slots
    .filter((slot) =>
      kind === OUTSTANDING ? !isSettled(slot) : slot.state.kind === kind
    )
    .map((slot) => toMonthDisplay(slot.month))
    .join(', ')

const describeAccount = (accountLabel: string, slots: Slot[]): string => {
  const parts = [
    ['uploaded', listMonths(slots, SLOT_KIND.received)],
    ['caught up', listMonths(slots, SLOT_KIND.caughtUp)],
    ['already on file', listMonths(slots, SLOT_KIND.onFile)],
    ['still needed', listMonths(slots, OUTSTANDING)],
  ]
    .filter(([, months]) => months)
    .map(([label, months]) => `${label} ${months}`)
  return `${accountLabel}: ${parts.join('; ')}.`
}

export const NOTHING_SENT_OUTCOME =
  'Closed the upload without sending a statement.'

export const DONE_FOR_NOW = 'Done for now'

/** What the member sees in place of the card; the outcome text stays what the model reads. */
export const toDisplay = (slots: Slot[]): WidgetDisplay | undefined =>
  slots.some(isSettled) ? undefined : { label: DONE_FOR_NOW }

export const UPLOAD_MORE_NOTE =
  'The rest can be uploaded later with Upload more statements on this card, or by asking for the upload again.'

export const NAME_MISSING_NOTE =
  'Tell the member which months are still needed and that catch-up cannot finish without them, in your one sentence before anything else.'

const describeAccounts = (slots: Slot[]): string => {
  const byAccount = new Map<number, Slot[]>()
  slots.forEach((slot) =>
    byAccount.set(slot.financialAccountId, [
      ...(byAccount.get(slot.financialAccountId) ?? []),
      slot,
    ])
  )
  return [...byAccount.values()]
    .map((accountSlots) =>
      describeAccount(accountSlots[0].accountLabel, accountSlots)
    )
    .join('\n')
}

export const toOutcome = (slots: Slot[]): string =>
  slots.some(isSettled) ? describeAccounts(slots) : NOTHING_SENT_OUTCOME

/**
 * The report "Done for now" sends. While a month is still needed it lists them,
 * even when nothing was sent, and says how to come back.
 */
export const toPausedOutcome = (slots: Slot[]): string => {
  if (!slots.some((slot) => !isSettled(slot))) return toOutcome(slots)
  const report = slots.some(isSettled)
    ? describeAccounts(slots)
    : `${NOTHING_SENT_OUTCOME}\n${describeAccounts(slots)}`
  return `${report}\n${NAME_MISSING_NOTE}\n${UPLOAD_MORE_NOTE}`
}

export const uploadStatement =
  (
    slot: Slot,
    file: File,
    user: { id: number; uuid: string },
    onProgress: (percent: number) => void
  ) =>
  async (dispatch: Dispatch): Promise<boolean> => {
    const result = await dispatch(
      userUploadDocument({
        file,
        user,
        documentType: UploadDocumentType.STATEMENT,
        statement: {
          statementMonth: slot.month,
          financialAccountIds: [slot.financialAccountId],
        },
        onProgress,
      })
    )
    return Boolean(result) && !(result && 'error' in result)
  }

const MAX_UPLOAD_MB = 25

/** Archives and executables, matched on the end of the file name. */
export const REFUSED_EXTENSIONS = [
  '.zip',
  '.rar',
  '.7z',
  '.tar',
  '.gz',
  '.tgz',
  '.bz2',
  '.exe',
  '.msi',
  '.dmg',
  '.pkg',
  '.app',
  '.apk',
  '.bat',
  '.cmd',
  '.sh',
  '.jar',
] as const

/** Why a file is refused before upload, or null when it may go up. */
export const refusalFor = (file: File): string | null => {
  const name = file.name.toLowerCase()
  if (REFUSED_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return "We can't accept this file type. Send a PDF, image, or spreadsheet."
  }
  const sizeMb = file.size / (1024 * 1024)
  if (sizeMb > MAX_UPLOAD_MB) {
    return `${file.name} is ${Math.round(sizeMb)} MB. The limit is ${MAX_UPLOAD_MB} MB.`
  }
  return null
}

/** Extraction reads PDFs only; anything else is filed for a bookkeeper. */
export const isReadableStatement = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
